import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Inbox, Check, RefreshCw, AlertTriangle } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';

// VanReceptionsInbox — pending stock receptions (TAR replenishments/transfers)
// for a van/warehouse. One-click "Recebi tudo" + optional discrepancy flow.
export const VanReceptionsInbox = ({ warehouseCode, onConfirmed }) => {
  const { refresh: refreshNotifications } = useNotifications();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  // Discrepancy modal
  const [discTransfer, setDiscTransfer] = useState(null);
  const [discQtys, setDiscQtys] = useState({});
  const [discNotes, setDiscNotes] = useState('');
  const [savingDisc, setSavingDisc] = useState(false);

  const fetchInbox = useCallback(async () => {
    if (!warehouseCode) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/store/transfers/inbox`, {
        params: { include_received: false, warehouse: warehouseCode, limit: 50 },
      });
      setTransfers((res.data?.transfers || []).filter(t => t.target_warehouse === warehouseCode));
    } catch { /* silent — section simply hides */ } finally {
      setLoading(false);
    }
  }, [warehouseCode]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const afterConfirm = () => {
    fetchInbox();
    refreshNotifications();
    if (onConfirmed) onConfirmed();
  };

  const confirmAll = async (transfer) => {
    try {
      setConfirmingId(transfer.id);
      await axios.post(`${API}/store/transfers/${transfer.id}/confirm-receipt`, {});
      toast.success(`Receção confirmada · ${transfer.sage_document_reference || ''}`);
      afterConfirm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao confirmar receção');
    } finally {
      setConfirmingId(null);
    }
  };

  const openDiscrepancy = (transfer) => {
    const qtys = {};
    (transfer.items || []).forEach(it => { qtys[it.sku] = it.quantity; });
    setDiscQtys(qtys);
    setDiscNotes('');
    setDiscTransfer(transfer);
  };

  const saveDiscrepancy = async () => {
    if (!discTransfer) return;
    try {
      setSavingDisc(true);
      await axios.post(`${API}/store/transfers/${discTransfer.id}/confirm-receipt`, {
        items: (discTransfer.items || []).map(it => ({
          sku: it.sku,
          received_quantity: parseInt(discQtys[it.sku]) || 0,
        })),
        notes: discNotes || null,
      });
      toast.success('Receção registada com divergências');
      setDiscTransfer(null);
      afterConfirm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registar receção');
    } finally {
      setSavingDisc(false);
    }
  };

  if (!transfers.length) return null;

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="mx-4 mt-3 space-y-2" data-testid="van-receptions-inbox">
      <div className="flex items-center gap-2">
        <Inbox className="h-4 w-4 text-amber-600" />
        <p className="text-sm font-semibold text-amber-800">
          Receções pendentes ({transfers.length})
        </p>
        {loading && <RefreshCw className="h-3.5 w-3.5 animate-spin text-amber-400" />}
      </div>
      {transfers.map(t => (
        <div
          key={t.id}
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2"
          data-testid={`reception-card-${t.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-amber-800">
                <strong>{t.source_warehouse}</strong> → esta carrinha · {fmtDate(t.ts)}
              </p>
              {t.sage_document_reference && (
                <p className="text-[11px] font-mono text-amber-600">{t.sage_document_reference}</p>
              )}
            </div>
            <Badge className="bg-amber-200 text-amber-900 flex-shrink-0">
              {(t.items || []).reduce((s, it) => s + (it.quantity || 0), 0)} un.
            </Badge>
          </div>
          <div className="text-xs text-slate-700 space-y-0.5">
            {(t.items || []).map(it => (
              <div key={it.sku} className="flex justify-between gap-2 min-w-0">
                <span className="truncate min-w-0">{it.name || it.sku}</span>
                <span className="font-mono font-semibold flex-shrink-0">{it.quantity}x</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              className="h-10 flex-1 bg-green-600 hover:bg-green-700 active:bg-green-800"
              disabled={confirmingId === t.id}
              onClick={() => confirmAll(t)}
              data-testid={`confirm-all-${t.id}`}
            >
              {confirmingId === t.id
                ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                : <Check className="h-4 w-4 mr-1.5" />}
              Recebi tudo
            </Button>
            <Button
              variant="outline"
              className="h-10 border-amber-300 text-amber-800"
              onClick={() => openDiscrepancy(t)}
              data-testid={`discrepancy-btn-${t.id}`}
            >
              <AlertTriangle className="h-4 w-4 mr-1" />
              Falta algo
            </Button>
          </div>
        </div>
      ))}

      {/* Discrepancy modal */}
      <Dialog open={!!discTransfer} onOpenChange={(open) => !open && setDiscTransfer(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle>Indicar divergências</DialogTitle>
            <DialogDescription>
              Confirma a quantidade realmente recebida de cada artigo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 min-w-0">
            {(discTransfer?.items || []).map(it => (
              <div key={it.sku} className="flex items-center justify-between gap-3 min-w-0">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs text-slate-500 truncate">{it.sku}</p>
                  <p className="text-sm truncate">{it.name}</p>
                  <p className="text-xs text-muted-foreground">Esperado: {it.quantity}</p>
                </div>
                <Input
                  type="number"
                  min={0}
                  value={discQtys[it.sku] ?? it.quantity}
                  onChange={(e) => setDiscQtys(prev => ({ ...prev, [it.sku]: e.target.value }))}
                  className="w-20 h-11 text-center font-bold font-mono"
                  data-testid={`disc-qty-${it.sku}`}
                />
              </div>
            ))}
            <Textarea
              placeholder="Notas (ex: caixa danificada, artigo em falta...)"
              value={discNotes}
              onChange={(e) => setDiscNotes(e.target.value)}
              className="text-sm"
              data-testid="disc-notes"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="h-11 w-full sm:w-auto" onClick={() => setDiscTransfer(null)}>
              Cancelar
            </Button>
            <Button
              className="h-11 w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
              onClick={saveDiscrepancy}
              disabled={savingDisc}
              data-testid="save-discrepancy-btn"
            >
              {savingDisc ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Registar receção
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VanReceptionsInbox;
